import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { useLocation } from 'react-router-dom';

type SuperuserTheme = 'light' | 'gruvbox-dark';

interface SuperuserThemeContextValue {
  theme: SuperuserTheme;
  setTheme: (theme: SuperuserTheme) => void;
}

const STORAGE_KEY = 'adj-superuser-theme';
const ROOT_ATTRIBUTE = 'data-superuser-theme';
const SuperuserThemeContext = createContext<SuperuserThemeContextValue | null>(null);

function readStoredTheme(): SuperuserTheme {
  if (typeof window === 'undefined') return 'light';
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'gruvbox-dark' ? 'gruvbox-dark' : 'light';
  } catch {
    return 'light';
  }
}

function isSuperuserPath(pathname: string): boolean {
  return pathname === '/superuser' || pathname.startsWith('/superuser/');
}

export default function SuperuserThemeProvider({ children }: PropsWithChildren) {
  const { pathname } = useLocation();
  const [theme, setThemeState] = useState<SuperuserTheme>(readStoredTheme);
  const active = isSuperuserPath(pathname);

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (active && theme === 'gruvbox-dark') {
      root.setAttribute(ROOT_ATTRIBUTE, theme);
    } else {
      root.removeAttribute(ROOT_ATTRIBUTE);
    }

    return () => {
      root.removeAttribute(ROOT_ATTRIBUTE);
    };
  }, [active, theme]);

  const setTheme = useCallback((nextTheme: SuperuserTheme) => {
    setThemeState(nextTheme);
    try {
      window.localStorage.setItem(STORAGE_KEY, nextTheme);
    } catch {
      // The visible choice still applies when browser storage is unavailable.
    }
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [setTheme, theme]);

  return <SuperuserThemeContext.Provider value={value}>{children}</SuperuserThemeContext.Provider>;
}

export function useSuperuserTheme(): SuperuserThemeContextValue {
  const value = useContext(SuperuserThemeContext);
  if (!value) throw new Error('useSuperuserTheme must be used inside SuperuserThemeProvider');
  return value;
}
